<template>
  <ReviewItemDetails
    class="border-b border-l border-neutral-border-default"
    :expansion-panel="expansionPanel"
    :is-metadata-expanded="isMetadataExpanded"
    :review-item="reviewItem"
  />
  <div
    v-if="activeAppraisalKey === 'imdrf'"
    class="border-b border-l border-mid-neutral-50 bg-white"
    :class="expansionPanel.isShown.value ? 'py-0' : 'items-center py-3'"
  >
    <div v-if="expansionPanel.isShown.value" class="flex flex-col divide-y divide-gray-200 w-151.5">
      <div
        v-for="(criterion, appraisalCriterionIndex) in review.entity.value?.plan?.appraisalCriteria"
        :key="criterion.id + appraisalCriterionIndex"
        class="px-4 py-3"
      >
        <div
          class="flex items-start justify-between gap-4 cursor-pointer"
          @click="toggleAnswerRow(criterion.id)"
        >
          <div class="flex-1">
            <p class="text-sm text-gray-700 mb-1">
              <span class="font-medium">{{ criterion.id }}</span> -
              {{ criterion.question }}
            </p>
            <p
              v-if="!isRowExpanded(criterion.id) && reviewItem.appraisal?.[criterion.id]"
              class="text-sm text-primary-text-default mt-1 ml-2"
            >
              {{ criterion.id }}
              {{
                criterion.answers?.findIndex((a) => a.id === reviewItem.appraisal?.[criterion.id]) +
                1
              }}
              -
              {{
                criterion.answers?.find((a) => a.id === reviewItem.appraisal?.[criterion.id])?.title
              }}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <CheckCircleIcon
              v-if="reviewItem.appraisal?.[criterion.id]"
              class="size-5 text-green-500 shrink-0"
            />
            <i-material-symbols-keyboard-arrow-down
              v-if="isRowExpanded(criterion.id)"
              class="size-4 text-gray-800 transition-transform duration-200"
            />
            <i-material-symbols-chevron-right
              v-else
              class="size-4 text-gray-800 transition-transform duration-200"
            />
          </div>
        </div>
        <ExecuteLockGuard v-if="isRowExpanded(criterion.id)">
          <div class="flex flex-col gap-1.5 ml-2 mt-2">
            <button
              v-for="answer in criterion.answers"
              :key="answer.id"
              class="flex items-center gap-2 text-left text-sm py-1 px-2 rounded transition-colors"
              :class="[
                reviewItem.appraisal?.[criterion.id] === answer.id
                  ? 'text-primary-text-default font-medium'
                  : 'text-gray-600 hover:bg-gray-100',
                reviewItem.state !== StudyState.INCLUDED
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer',
              ]"
              :disabled="reviewItem.state !== StudyState.INCLUDED || review.isReviewReadonly.value"
              @click.stop="
                reviewItem.state === StudyState.INCLUDED &&
                !review.isReviewReadonly.value &&
                (reviewItem.appraisal?.[criterion.id] === answer.id
                  ? clearStudyAppraisal(reviewItem.id, criterion.id)
                  : appraiseStudy(reviewItem.id, criterion.id, answer.id))
              "
            >
              <span
                class="size-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                :class="
                  reviewItem.appraisal?.[criterion.id] === answer.id
                    ? 'border-primary-text-default bg-primary-text-default'
                    : 'border-gray-300'
                "
              >
                <span
                  v-if="reviewItem.appraisal?.[criterion.id] === answer.id"
                  class="size-1.5 rounded-full bg-white"
                />
              </span>
              <span>{{ answer.title }}</span>
            </button>
          </div>
        </ExecuteLockGuard>
      </div>
    </div>
    <div v-else class="gap-1.5 flex px-2">
      <ExecuteLockGuard
        v-for="(criterion, appraisalCriterionIndex) in review.entity.value?.plan?.appraisalCriteria"
        :key="criterion.id + appraisalCriterionIndex"
      >
        <FloatingMenu placement="bottom">
          <template #activator="{ show }">
            <div
              v-tooltip="
                criterion.answers?.find((a) => a.id === reviewItem.appraisal?.[criterion.id])?.title
              "
            >
              <StudyActionButton
                :appraisal-classes="'px-2 py-2.5 w-15'"
                :state="
                  reviewItem.state === StudyState.INCLUDED && reviewItem.appraisal?.[criterion.id]
                    ? ButtonState.appraised
                    : reviewItem.state !== StudyState.INCLUDED
                      ? ButtonState.disabled
                      : ButtonState.inactive
                "
                :is-review-read-only="review.isReviewReadonly.value"
                @click="show()"
              >
                <p v-if="!reviewItem.appraisal?.[criterion.id]">
                  {{ criterion.id }}
                </p>
                <p v-else>
                  {{
                    criterion.id +
                    (criterion.answers?.findIndex(
                      (a) => a.id === reviewItem.appraisal?.[criterion.id],
                    ) +
                      1)
                  }}
                </p>
              </StudyActionButton>
            </div>
          </template>
          <template #content="{ hide }">
            <div class="flex flex-col w-[350px] divide-y divide-gray-200">
              <p class="px-4 py-2 text-left font-bold">
                {{ criterion.question }}
              </p>
              <button
                v-for="(answer, answerIndex) in criterion.answers"
                :key="answer.id"
                class="flex flex-row px-4 py-2 text-left w-full gap-4"
                :class="
                  reviewItem.appraisal?.[criterion.id] === answer.id
                    ? 'bg-slate-500 text-white '
                    : 'hover:bg-gray-200'
                "
                @click="
                  reviewItem.appraisal?.[criterion.id] === answer.id
                    ? clearStudyAppraisal(reviewItem.id, criterion.id, hide)
                    : appraiseStudy(reviewItem.id, criterion.id, answer.id, hide)
                "
              >
                <p>{{ criterion.id + (answerIndex + 1) }}</p>
                <p>{{ answer.title }}</p>
              </button>
            </div>
          </template>
        </FloatingMenu>
      </ExecuteLockGuard>
    </div>
  </div>
  <div
    v-else-if="activeAppraisalKey === 'oxford'"
    class="border-b border-l border-mid-neutral-50 bg-white items-center py-3"
  >
    <div class="w-full justify-evenly gap-2 flex px-4">
      <OxfordLevelOfEvidence :study="reviewItem" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import useLoading from "@app/composables/use-loading";
import useSnackbar from "@app/composables/use-snackbar";
import { ButtonState, SnackbarState, StudyState } from "@app/types";
import { errorMessage } from "@app/utils/error-message";
import { ReviewKey } from "@app/views/Review/use-review";
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import { Id } from "@core/domain/types/id.type";
import { HttpException } from "@core/exceptions/http.exception";
import { injectStrict } from "@app/utils/injectStrict";
import OxfordLevelOfEvidence from "./OxfordLevelOfEvidence.vue";
import StudyActionButton from "../../StudyActionButton.vue";
import ExecuteLockGuard from "../../ExecuteLockGuard.vue";
import useExpansionPanel from "@app/composables/use-expansion-panel";
import ReviewItemDetails from "@app/views/Review/Execute/ReviewItemDetails/ReviewItemDetails.vue";
import { ref } from "vue";
import { CheckCircleIcon } from "@heroicons/vue/16/solid";

const review = injectStrict(ReviewKey);

const expansionPanel = useExpansionPanel();
const snackbar = useSnackbar();
const loading = useLoading();
const expandedAnswerRows = ref<Set<string>>(new Set());

function toggleAnswerRow(id: string) {
  if (expandedAnswerRows.value.has(id)) {
    expandedAnswerRows.value.delete(id);
  } else {
    expandedAnswerRows.value.add(id);
  }
}

function isRowExpanded(id: string) {
  return expandedAnswerRows.value.has(id);
}

defineProps<{
  reviewItem: ReviewItem;
  isMetadataExpanded: boolean;
  activeAppraisalKey?: "imdrf" | "oxford" | null;
}>();

async function appraiseStudy(
  studyId: Id,
  criterionId: string,
  gradeId: string,
  cb: () => void = () => {},
) {
  try {
    loading.start();
    await review.appraiseStudy(studyId, { criterionId, gradeId });
    snackbar.success("Success");
    cb();
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loading.stop();
  }
}

async function clearStudyAppraisal(studyId: Id, criterionId: string, cb: () => void = () => {}) {
  try {
    loading.start();
    await review.clearStudyAppraisal(studyId, criterionId);
    snackbar.success("Success");
    cb();
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loading.stop();
  }
}
</script>
